/**
 * «HVEM ER MED?» — organisasjonens medlemmer med en avkrysning hver (QA13 punkt 32).
 *
 * Atle: «Intuitivt i selve utformingen. Hjelpetekster som forklarer en uklar løsning er ikke godt
 * nok.» Derfor er det ingen forklaring her, bare det man trenger for å velge: navn, tittel og rolle
 * på hver rad, et søk, «Velg alle», og tallet på hvor mange som er valgt. Raden er hele
 * treffflaten. Der et forslag er krysset av på forhånd, står det i én linje hvor det kommer fra.
 *
 * «Velg alle» gjelder radene som vises. Med et søk på «bar» velger den de tre som heter noe med
 * bar, ikke hele huset — det er det man ser man trykker på.
 *
 * Brukt i tre dialoger: «Ny festival», «Ny årgang» og «Legg til person». Lista eies av kalleren
 * (hvem som er med er en del av skjemaet der), komponenten tegner og melder fra.
 */
import type { MemberChoiceDto } from '@tvitvi/shared';
import { useId, useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { TextField } from '@/components/ui/TextField';
import { dynamicKey } from '@/i18n/types';

export interface MemberChooserProps {
  members: readonly MemberChoiceDto[];
  chosen: ReadonlySet<string>;
  onChange: (next: ReadonlySet<string>) => void;
  /** Én linje over lista når noe er krysset av på forhånd: hvor forslaget kommer fra. */
  proposal?: string | null;
  'data-testid'?: string;
}

function matches(member: MemberChoiceDto, needle: string): boolean {
  if (needle === '') return true;
  return [member.displayName, member.jobTitle ?? '']
    .join(' ')
    .toLocaleLowerCase('nb')
    .includes(needle);
}

export function MemberChooser({
  members,
  chosen,
  onChange,
  proposal = null,
  'data-testid': testId = 'member-chooser',
}: MemberChooserProps): ReactNode {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const listId = useId();

  const needle = search.trim().toLocaleLowerCase('nb');
  const visible = members.filter((member) => matches(member, needle));
  const allVisibleChosen =
    visible.length > 0 && visible.every((member) => chosen.has(member.personId));

  const toggle = (personId: string): void => {
    const next = new Set(chosen);
    if (next.has(personId)) next.delete(personId);
    else next.add(personId);
    onChange(next);
  };

  const toggleAll = (): void => {
    const next = new Set(chosen);
    for (const member of visible) {
      if (allVisibleChosen) next.delete(member.personId);
      else next.add(member.personId);
    }
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {proposal === null ? null : (
        <p className="text-sm text-muted" data-testid={`${testId}-proposal`}>
          {proposal}
        </p>
      )}

      <Field label={t('memberChooser.search')}>
        {(control) => (
          <TextField
            {...control}
            type="search"
            value={search}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
            aria-controls={listId}
            data-testid={`${testId}-search`}
          />
        )}
      </Field>

      <div className="flex items-center justify-between gap-2">
        <span className="numeric text-sm text-muted" data-testid={`${testId}-count`}>
          {t('memberChooser.count', { count: chosen.size, total: members.length })}
        </span>
        <Button
          variant="quiet"
          size="sm"
          disabled={visible.length === 0}
          onClick={toggleAll}
          data-testid={`${testId}-all`}
        >
          {allVisibleChosen ? t('memberChooser.noneOfThem') : t('memberChooser.all')}
        </Button>
      </div>

      {visible.length === 0 ? (
        <p className="py-2 text-sm text-muted" data-testid={`${testId}-empty`}>
          {members.length === 0 ? t('memberChooser.noMembers') : t('memberChooser.noMatch')}
        </p>
      ) : (
        <ul
          id={listId}
          className="flex flex-col divide-y divide-subtle"
          data-testid={`${testId}-list`}
        >
          {visible.map((member) => (
            <li key={member.personId}>
              <label
                className="flex min-h-11 cursor-pointer items-center gap-3 px-1 py-1.5"
                data-testid={`${testId}-row-${member.personId}`}
              >
                <input
                  type="checkbox"
                  className="check-box ring-focus shrink-0"
                  checked={chosen.has(member.personId)}
                  onChange={() => toggle(member.personId)}
                  data-testid={`${testId}-check-${member.personId}`}
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm text-default">{member.displayName}</span>
                  {member.jobTitle === null ? null : (
                    <span className="truncate text-xs text-muted">{member.jobTitle}</span>
                  )}
                </span>
                <Badge variant="plain">{t(dynamicKey(`enums.org_role.${member.role}`))}</Badge>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
